import { getQueryString, handleError, page_size } from "../apiUtils";
import { createAxiosInstance } from "../apiUtils";
import { AxiosRequestConfig } from "axios";

export async function getConnectionsList(token: string, query: object) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString(query)
    try {
        const resp = await axiosInstance.get("/v2/connections/?pager=limit&limit=" + page_size + "&" + query_string);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getConnectionProviderTypes(token: string, query?: object, signal?: AbortSignal) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString({ limit: page_size, ...(query || {}) })
    try {
        const resp = await axiosInstance.get("/v2/integrations/types/?is_provider=true&pager=limit&" + query_string, { signal });
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// Bidirectional flows (GUNDI-5595): the reverse direction is a separate route whose
// provider is one of this connection's destinations and whose destination is this
// connection's provider. `?destination_id=` is the only filter that can find it —
// there is no provider_id filter on /v2/connections/.
export async function getReverseConnections(token: string, destination_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/connections/?destination_id=" + destination_id);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// A connection's id IS its provider integration's id, so this answers "is this
// record the source of a connection?" without a provider_id filter: 200 means yes,
// 404 means it is not used as a provider. Returns null instead of throwing so
// callers can treat "not a provider" as an ordinary answer.
export async function getProviderConnection(token: string, integration_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/connections/" + integration_id + "/");
        return resp.data;
    } catch (err: any) {
        if (err?.response?.status === 404) return null;
        handleError(err);
        return null;
    }
}

export type IntegrationRoles = {
    asProvider: { id: string, name: string }[];
    asDestination: { id: string, name: string }[];
};

// Every connection, one page at a time, inverted into "which role does each
// integration play, and in what". No endpoint carries per-integration role data, so
// the Connected Systems list derives it here once and reuses it for every row
// instead of asking per record.
export async function loadIntegrationRoleMap(token: string): Promise<Record<string, IntegrationRoles>> {
    const map: Record<string, IntegrationRoles> = {};
    const bucket = (id: string) => (map[id] = map[id] || { asProvider: [], asDestination: [] });
    let offset = 0;
    // Bounded rather than trusting `next` forever.
    for (let page = 0; page < 50; page += 1) {
        const data = await getConnectionsList(token, { offset });
        const results: any[] = data?.results || [];
        results.forEach((connection: any) => {
            const label = { id: connection.id, name: connection.provider?.name || connection.id };
            if (connection.provider?.id) bucket(connection.provider.id).asProvider.push(label);
            (connection.destinations || []).forEach((d: any) => {
                if (d?.id) bucket(d.id).asDestination.push(label);
            });
        });
        if (!data?.next || results.length === 0) return map;
        offset += results.length;
    }
    // Hit the page cap with pages still to go: the map is partial, so some records
    // will look less used than they are. Say so rather than failing silently.
    console.log("connection role map truncated at 50 pages; some roles may be missing");
    return map;
}

// Creates the reverse-direction route. `data_providers` accepts EXISTING integration
// ids, so the same two integration records serve both directions (GUNDI-5595) — no
// duplicate provider record is created. Note this does not touch integration.default_route.
export async function createRoute(token: string, payload: {
    name: string,
    owner: string,
    data_providers: string[],
    destinations: string[],
}) {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.post("/v2/routes/", payload);
    return resp.data;
}

export async function deleteRoute(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.delete("/v2/routes/" + route_id + "/");
    return resp.data;
}

// Which of these integrations have an action of the given type? Used to gate the
// "enable reverse direction" toggles: a site can only send data back if its type
// registers a pull action. One call instead of N integration lookups.
export async function getIntegrationIdsWithActionType(token: string, ids: string[], actionType: string) {
    if (ids.length === 0) return [];
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get(`/v2/integrations/?id__in=${ids.join(",")}&action_type=${actionType}`);
        return (resp.data?.results || resp.data || []).map((i: any) => i.id);
    } catch (err) {
        console.log("capability lookup failed", err);
        return [];
    }
}

export async function getConnectionDetails(token: string, connection_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/connections/" + connection_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getConnectionApiKey(token: string, connection_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/integrations/" + connection_id + "/api-key/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getIntegrationDetails(token: string, connection_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/integrations/" + connection_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function getConnectionLog(token: string, query: object, signal?: AbortSignal, timeout?: number) {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString(query)
    try {
        const config: AxiosRequestConfig = {};
        if (signal) {
            config.signal = signal;
        }
        if (timeout !== undefined) {
            config.timeout = timeout;
        }
        const resp = await axiosInstance.get("/v2/logs/?pager=cursor&" + query_string, config);
        return resp.data;
    } catch (err: any) {
        // Abort and timeout are expected cases; let the caller handle them directly.
        if (err && typeof err === "object") {
            const errorCode = (err as any).code;
            const errorName = (err as any).name;
            if (errorName === "AbortError" || errorCode === "ERR_CANCELED" || errorCode === "ECONNABORTED") {
                throw err;
            }
        }
        handleError(err);
    }
}

export async function getLogDetails(token: string, log_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/logs/" + log_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function createNewConnection(token: string, new_connection: object) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.post("/v2/integrations/", new_connection);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// Attach/detach destinations on a connection by PATCHing the underlying route with
// `{ destinations: [...] }`. Despite the endpoint living under `/v2/routes/`, this is
// scoped to destination membership only — see the `// TRANSFORMATION RULES` section
// below for the functions that PATCH the route's `configuration.data`.
export async function addConnectionDestinations(token: string, route_id: string, payload: { destinations: string[] }) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.patch("/v2/routes/" + route_id + "/", payload);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// TRANSFORMATION RULES (route configuration)
// Field mappings live in `route.configuration.data.field_mappings`. The endpoint is the
// same `/v2/routes/{id}/` used for destination membership, but the payload shape is
// different: `{ configuration: { data: {...} } }`.

export async function getRouteConfiguration(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.get("/v2/routes/" + route_id + "/");
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

// The backend updates the route_configuration linked to this route in place. We don't
// need to pass `configuration.id` — the route already references its configuration —
// and `data` is replaced wholesale, so callers must build the full data they want
// persisted (see fieldMappingTypes.buildConfigWithRule for the merge helper). 400
// validation errors propagate via the thrown axios error so the caller can surface
// them on the right field.
export async function updateRouteConfiguration(
    token: string,
    route_id: string,
    payload: { name: string; data: any }
) {
    const axiosInstance = await createAxiosInstance(token);
    // We intentionally rethrow so the caller can inspect 400 response bodies for
    // field-level validation errors. Other errors still go through handleError.
    const resp = await axiosInstance.patch("/v2/routes/" + route_id + "/", {
        configuration: payload,
    });
    return resp.data;
}

// Unlinks the configuration from the route and hard-deletes it from the DB when
// no other route references it. Use this when the user removes the last rule —
// otherwise you'd PATCH the route with empty data and leave an orphan row behind.
// Idempotent: if the route has no configuration the backend still returns 204.
export async function deleteRouteConfiguration(token: string, route_id: string) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        await axiosInstance.delete("/v2/routes/" + route_id + "/configuration/");
    } catch (err) {
        handleError(err);
    }
}

export async function updateConnection(token: string, integration_id: string, newConfigs: any) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.patch("/v2/integrations/" + integration_id + "/", newConfigs);
        return resp.data;
    } catch (err) {
        handleError(err);
    }
}

export async function testConnectionAction(token: string, integration_id: string, action_id: string, data: any) {
    const axiosInstance = await createAxiosInstance(token);
    try {
        const resp = await axiosInstance.post("/v2/integrations/" + integration_id + "/actions/" + action_id + "/execute/", data);
        return { data: resp.data, status: resp.status };
    } catch (err) {
        handleError(err);
    }
}